import { useEffect } from 'react';
import { useSupervisorDetails } from '../../hooks/dashboard/dashboard';
import Pagination from '../common/pagination';

const shimmerCSS = `
  @keyframes shimmer {
    0% { background-position: -800px 0; }
    100% { background-position: 800px 0; }
  }
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .skeleton {
    background: linear-gradient(90deg, #f0e8eb 25%, #fdf5f8 50%, #f0e8eb 75%);
    background-size: 800px 100%;
    animation: shimmer 1.6s infinite linear;
    border-radius: 6px;
  }
  .db-table-row:hover td {
    background: #fdf0f4 !important;
  }
  .db-search-input:focus {
    outline: none;
    border-color: #a06070 !important;
    box-shadow: 0 0 0 3px rgba(88,31,55,0.10) !important;
  }
  .fade-in {
    animation: fadeIn 0.4s ease both;
  }
`;

function Dashboard({ onBack, onSubmitSuccess }) {
    const {
        supervisor,
        profileLoading,
        profileError,
        disciplines,
        currentPage,
        lastPage,
        total,
        search,
        disciplinesLoading,
        handlePageChange,
        handleSearchChange,
    } = useSupervisorDetails();

    const statusColor = (status) => {
        if (status === 'Active') return { background: '#581f37', color: '#fff' };
        if (status === 'Pending') return { background: '#f5f0f2', color: '#7a3050', border: '1px solid #e0c8d0' };
        return { background: '#f0f0f0', color: '#666' };
    };

    useEffect(() => {
        const id = 'db-shimmer-css';
        if (!document.getElementById(id)) {
            const tag = document.createElement('style');
            tag.id = id;
            tag.textContent = shimmerCSS;
            document.head.appendChild(tag);
        }
        return () => {
            const el = document.getElementById(id);
            if (el) el.remove();
        };
    }, []);

    const Banner = () => (
        <div className="position-relative overflow-hidden px-4 px-md-5 py-4" style={{ background: 'linear-gradient(135deg, #581f37 0%, #7a2d4e 60%, #6b2444 100%)' }}>
            <div className="position-absolute" style={{ left: -80, bottom: -80, width: 260, height: 260, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
            <div className="position-absolute" style={{ right: -60, top: -60, width: 300, height: 300, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />
            <div className="position-relative" style={{ zIndex: 2 }}>
                <span className="d-inline-block mb-3" style={{ fontSize: 10, letterSpacing: '0.22em', color: '#f0c8d8', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.18)', padding: '4px 14px', borderRadius: 3 }}>
                    Dashboard
                </span>
                <h1 className="mb-1 fw-bold text-white" style={{ fontSize: 'clamp(22px, 4vw, 32px)', letterSpacing: '-0.6px' }}>
                    Internship Supervisor
                </h1>
                <p className="mb-0" style={{ fontSize: 14, color: 'rgba(255,255,255,0.58)' }}>
                    Manage disciplines, students, and internship progress
                </p>
            </div>
        </div>
    );

    if (profileLoading) {
        return (
            <div className="min-vh-100 pb-5" style={{ background: '#f5f0f2', fontFamily: "'DM Sans', Arial, sans-serif" }}>
                <Banner />
                <div className="container-lg px-3 px-md-4" style={{ maxWidth: 900 }}>
                    <div className="card border-0 shadow-sm mt-n3 mb-4 fade-in" style={{ borderLeft: '4px solid #581f37', borderRadius: 12 }}>
                        <div className="card-body p-3 p-md-4">
                            <div className="d-flex align-items-center gap-3">
                                <div className="skeleton flex-shrink-0" style={{ width: 56, height: 56, borderRadius: '50%' }} />
                                <div className="flex-grow-1">
                                    <div className="skeleton mb-2" style={{ height: 18, width: '45%' }} />
                                    <div className="skeleton mb-2" style={{ height: 13, width: '65%' }} />
                                    <div className="skeleton" style={{ height: 13, width: '50%' }} />
                                </div>
                                <div className="skeleton flex-shrink-0" style={{ height: 48, width: 90, borderRadius: 10 }} />
                            </div>
                        </div>
                    </div>

                    <div className="card border-0 shadow-sm" style={{ borderRadius: 12 }}>
                        <div className="card-body p-0">
                            <div className="d-flex justify-content-between align-items-center px-4 py-3 border-bottom">
                                <div className="skeleton" style={{ height: 16, width: 160 }} />
                                <div className="skeleton" style={{ height: 16, width: 60, borderRadius: 20 }} />
                            </div>
                            <div className="px-4 py-3 border-bottom" style={{ background: '#fffafb' }}>
                                <div className="skeleton ms-auto" style={{ height: 38, width: 260, borderRadius: 8 }} />
                            </div>
                            <div style={{ background: '#581f37', padding: '12px 20px', display: 'flex', gap: 40 }}>
                                {['48px', '220px', '130px', '90px'].map((w, i) => (
                                    <div key={i} style={{ height: 11, width: w, background: 'rgba(255,255,255,0.2)', borderRadius: 4 }} />
                                ))}
                            </div>
                            {[...Array(5)].map((_, i) => (
                                <div key={i} className="d-flex align-items-center" style={{ gap: 40, padding: '14px 20px', background: i % 2 === 0 ? '#fff' : '#faf5f7', borderBottom: '1px solid #f0e8eb' }}>
                                    <div className="skeleton" style={{ height: 13, width: 28, borderRadius: 3 }} />
                                    <div className="skeleton" style={{ height: 13, width: 200, borderRadius: 3 }} />
                                    <div className="skeleton" style={{ height: 13, width: 80, borderRadius: 3 }} />
                                    <div className="skeleton" style={{ height: 24, width: 72, borderRadius: 20 }} />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const profileData = (profileError || !supervisor)
        ? { name: '—', college: '—', email: '—', position: '—' }
        : supervisor;

    return (
        <div className="min-vh-100 pb-5" style={{ background: '#f5f0f2', fontFamily: "'DM Sans', Arial, sans-serif" }}>
            <Banner />

            <div className="container-lg px-3 px-md-4" style={{ maxWidth: 900 }}>

                <div className="card border-0 shadow-sm mt-n3 mb-4 fade-in" style={{ borderLeft: '4px solid #581f37', borderRadius: 12 }}>
                    <div className="card-body p-3 p-md-4">
                        <div className="d-flex align-items-center gap-3 flex-wrap">
                            <div className="d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 56, height: 56, borderRadius: '50%', background: 'linear-gradient(145deg, #581f37 0%, #8a3055 100%)', boxShadow: '0 0 0 3px #f5e4ec' }}>
                                <UserIcon />
                            </div>

                            <div className="flex-grow-1 min-width-0">
                                <p className="mb-0 fw-bold" style={{ fontSize: 18, color: '#1a1a1a', wordBreak: 'break-word' }}>
                                    {profileData.name}
                                </p>
                                <div className="d-flex flex-column gap-1 mt-1">
                                    <span className="d-flex align-items-start gap-2 text-secondary" style={{ fontSize: 13 }}>
                                        <CollegeIcon />
                                        {profileData.college}
                                    </span>
                                    <span className="d-flex align-items-start gap-2 text-secondary" style={{ fontSize: 13 }}>
                                        <EmailIcon />
                                        {profileData.email}
                                    </span>
                                </div>
                            </div>

                            <div className="text-center flex-shrink-0 px-3 py-2 rounded" style={{ background: '#fdf5f8', border: '1px solid #f0d8e4' }}>
                                <p className="mb-0 fw-bold" style={{ fontSize: 24, color: '#581f37', lineHeight: 1 }}>
                                    {total}
                                </p>
                                <p className="mb-0 text-uppercase fw-semibold" style={{ fontSize: 10, color: '#a06070', letterSpacing: '0.06em' }}>
                                    Disciplines
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card border-0 shadow-sm" style={{ borderRadius: 12, overflow: 'hidden' }}>
                    <div className="d-flex align-items-center justify-content-between px-4 py-3 border-bottom flex-wrap gap-2">
                        <h6 className="mb-0 fw-bold text-sm">
                            Mapped Disciplines
                        </h6>
                        <span className="text-muted rounded-pill px-3 py-1" style={{ fontSize: 12, background: '#f7f4f5'}}>
                            {total} total
                        </span>
                    </div>

                    <div className="d-flex justify-content-end px-4 py-3 border-bottom" style={{ background: '#fffafb', borderBottomColor: '#581f37', borderBottomWidth: 2, borderBottomStyle: 'solid' }}>
                        <div className="d-flex align-items-center gap-2 bg-white rounded" style={{ border: '1.5px solid #e8d0da', padding: '7px 12px', minWidth: 220, maxWidth: 300, width: '100%' }}>
                            <SearchIcon />
                            <input
                                type="text"
                                placeholder="Search disciplines…"
                                value={search}
                                onChange={(e) => handleSearchChange(e.target.value)}
                                className="db-search-input border-0 bg-transparent flex-grow-1"
                                style={{ fontSize: 13, outline: 'none', color: '#333' }}
                            />
                            {search && (
                                <button
                                    onClick={() => handleSearchChange('')}
                                    className="border-0 bg-transparent p-0"
                                    style={{ color: '#b08090', fontSize: 18, lineHeight: 1, cursor: 'pointer' }}
                                    title="Clear search"
                                >×</button>
                            )}
                        </div>
                    </div>

                    <div className="table-responsive m-3">
                        <table className="display dataTable table-striped table-bordered">
                            <thead className="text-center">
                                <tr className="bg-primary">
                                    <th width="6%" className="text-center"></th>
                                    <th>Discipline</th>
                                    <th className="text-center">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {disciplinesLoading ? (
                                    [...Array(5)].map((_, i) => (
                                        <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#faf5f7' }}>
                                            <td className="px-4 py-3" style={{ width: 56 }}>
                                                <div className="skeleton" style={{ height: 13, width: 28, borderRadius: 3 }} />
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="skeleton" style={{ height: 13, width: `${55 + (i * 7) % 30}%`, maxWidth: 260, borderRadius: 3 }} />
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <div className="skeleton mx-auto" style={{ height: 24, width: 72, borderRadius: 20 }} />
                                            </td>
                                        </tr>
                                    ))
                                ) : disciplines.length === 0 ? (
                                    <tr>
                                        <td colSpan={4} className="px-4 text-center" style={{ padding: '52px 20px' }}>
                                            <EmptyIcon />
                                            <p className="mb-0 mt-2">
                                                {search ? `No disciplines matches"${search}".` : 'No disciplines found.'}
                                            </p>
                                        </td>
                                    </tr>
                                ) : (
                                    disciplines.map((d, i) => (
                                        <tr
                                            key={d.id ?? i}
                                            className="db-table-row"
                                        >
                                            <td data-label="No" className="px-4 py-3">
                                                {String(i + 1).padStart(2, '0')}
                                            </td>
                                            <td data-label="Discipline" className="px-4 py-3">
                                                {d.discipline_name ?? d.name}
                                            </td>
                                            <td data-label="Status" className="px-4 py-3 text-center">
                                                <span style={{
                                                    display: 'inline-block',
                                                    padding: '4px 14px',
                                                    borderRadius: 20,
                                                    fontSize: 11,
                                                    fontWeight: 700,
                                                    letterSpacing: '0.06em',
                                                    textTransform: 'uppercase',
                                                    ...statusColor(d.status)
                                                }}>
                                                    {d.status}
                                                </span>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {!disciplinesLoading && lastPage > 1 && (
                        <div className="d-flex justify-content-end px-4 py-3 border-top" style={{ background: '#fffafb' }}>
                            <Pagination
                                currentPage={currentPage}
                                lastPage={lastPage}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
}

function UserIcon() {
    return (
        <svg width="30" height="30" viewBox="0 0 48 48" fill="none">
            <circle cx="24" cy="18" r="11" fill="#fff" fillOpacity="0.95" />
            <ellipse cx="24" cy="44" rx="18" ry="11" fill="#fff" fillOpacity="0.95" />
        </svg>
    );
}
function CollegeIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: 2, flexShrink: 0 }}>
            <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
        </svg>
    );
}
function EmailIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: 2, flexShrink: 0 }}>
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
            <polyline points="22,6 12,13 2,6" />
        </svg>
    );
}
function SearchIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#b08090" strokeWidth="2" style={{ flexShrink: 0 }}>
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
    );
}
function EmptyIcon() {
    return (
        <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#d4b8c0" strokeWidth="1.3">
            <rect x="3" y="3" width="18" height="18" rx="3" />
            <line x1="3" y1="9" x2="21" y2="9" />
            <line x1="9" y1="21" x2="9" y2="9" />
        </svg>
    );
}

export default Dashboard;